import React, { Suspense, useState } from 'react';
import xlsx from 'json-as-xlsx';
import { useToast } from 'use-toast-mui';

import {
  dateStringFormatter,
  excelFormatString_Currency_5Decimals,
} from '../../../components/gridUtils';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';

import FuturesEntriesDisplay from './FuturesEntriesDisplay/FuturesEntriesDisplay';
import FuturesEntryModal from './FuturesEntryDetails/FuturesEntryModal';
import useFuturesEntries from './useFuturesEntries';

export default function FuturesEntriesPage() {
  const toast = useToast();
  const {
    userName,
    filterForm,
    rowCountState,
    setFilterForm,
    rows,
    getAllFilteredFutureEntries,
    isLoadingRows,
    futuresContracts,
    commodities,
    brokers,
    tradeAccountSummaries,
    paginationModel,
    onPaginationModelChange,
    onSortModelChange,
    onFilterModelChange,
  } = useFuturesEntries();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [entryToEdit, setEntryToEdit] = useState<string>('');
  const handleNewButtonClick = () => {
    setEntryToEdit('');
    setIsModalOpen(true);
  };
  const handleEditButtonClick = (id: string) => {
    setEntryToEdit(id);
    setIsModalOpen(true);
  };
  const handleModalClose = () => {
    setEntryToEdit('');
    setIsModalOpen(false);
  };

  const handleExportButtonClick = async (onFinish: () => void) => {
    try {
      const futuresData = (await getAllFilteredFutureEntries()).data?.result?.entries;
      if (futuresData) {
        const data = [
          {
            sheet: 'Futures Entries',
            columns: [
              { label: 'ID', value: 'id' },
              { label: 'Commodity', value: 'commodity' },
              { label: 'Futures Contract', value: 'futuresContract' },
              { label: 'Contracts', value: 'contracts' },
              { label: 'Month', value: 'optionMonth' },
              { label: 'Position Period', value: 'positionMonth' },
              {
                label: 'Futures Price',
                value: 'futuresPrice',
                format: excelFormatString_Currency_5Decimals,
              },
              { label: 'Broker', value: 'broker' },
              { label: 'Order Number', value: 'brokerOrderNumber' },
              { label: 'Trade Account', value: 'tradeAccount' },
              { label: 'Comment', value: 'comment' },
              { label: 'Entry User', value: 'entryUser' },
              {
                label: 'Entry Date Time',
                value: (r: any) => dateStringFormatter(r?.entryDateTime),
              },
            ],
            content: futuresData,
          },
        ];
        const settings = {
          fileName: 'Futures Entries Export',
        };
        xlsx(data, settings);
      } else {
        toast.error('Export failed.');
      }
    } finally {
      onFinish();
    }
  };

  return (
    <>
      <main data-testid="FuturesEntriesPage" style={{ minWidth: 1400, padding: '24px' }}>
        <FuturesEntriesDisplay
          rows={rows}
          loading={isLoadingRows}
          rowCountState={rowCountState}
          futuresContracts={futuresContracts}
          commodities={commodities}
          brokers={brokers}
          tradeAccountSummaries={tradeAccountSummaries}
          filterForm={filterForm}
          setFilterForm={setFilterForm}
          paginationModel={paginationModel}
          setPaginationModel={onPaginationModelChange}
          onSortModelChange={onSortModelChange}
          onFilterModelChange={onFilterModelChange}
          onNewButtonClick={handleNewButtonClick}
          onEditButtonClick={handleEditButtonClick}
          onExportButtonClick={handleExportButtonClick}
        ></FuturesEntriesDisplay>
        {isModalOpen && (
          <Suspense fallback={<LoadingSpinner />}>
            <FuturesEntryModal
              selectedEntryId={entryToEdit}
              onClose={handleModalClose}
              userName={userName}
            ></FuturesEntryModal>
          </Suspense>
        )}
      </main>
    </>
  );
}
